import {
  useAssignBeaconMutation,
  useAssignRouteToTaskMutation,
  useAssignSlimMutation,
} from 'app/api';
import { IEmployee, IImplement } from 'app/containers/Garage/types';
import { handleError } from 'app/containers/Global/slice/helpers';
import { selectSelectedEnvId } from 'app/containers/HiikoEnv/slice/selectors';
import { useGetRoutesWithoutTaskFromState } from 'app/containers/Task/pages/RoutesWithoutTaskPage/hooks/useGetRoutesWithoutTaskFromState';
import {
  IRouteWithSelectedImplement,
  RoutesTableItemType,
} from 'app/containers/Task/slice/types';
import { RouteConstants } from 'app/routes';
import { useSelector } from 'react-redux';
import { generatePath, useHistory } from 'react-router-dom';
import { showAddRouteToTaskSuccessNotification } from 'utils/notifications';
import { mapSelectedImplementsEmployeeAndRoouteToPayload } from '../AddRouteToTaskDrawer.helpers';

export const useAddRoutesToTask = ({
  employeeArray,
  implementArray,
  groupedRoutes,
  routeTableItems,
  selectedTaskId,
  rowsWithSelectedImplement,
}: {
  groupedRoutes: RoutesTableItemType[];
  implementArray?: IImplement[];
  employeeArray?: IEmployee[];
  routeTableItems: RoutesTableItemType[];
  selectedTaskId: number | null;
  rowsWithSelectedImplement: IRouteWithSelectedImplement[];
}) => {
  const envId = useSelector(selectSelectedEnvId) as number;
  const history = useHistory();

  const { routes } = useGetRoutesWithoutTaskFromState();

  const [assignRouteToTask] = useAssignRouteToTaskMutation();

  // implement
  const [assignBeaconToRoute] = useAssignBeaconMutation();

  // employee
  const [assignSlimToRoute] = useAssignSlimMutation();

  const onMoveRoutesToTask = async () => {
    try {
      if (!selectedTaskId) {
        return;
      }

      const routesData = mapSelectedImplementsEmployeeAndRoouteToPayload({
        groupedRoutes,
        rowsWithSelectedImplement,
        employeeArray,
        implementArray,

        routeTableItems,
      });

      const routeIds = routesData.map(routeData => routeData.routeId);

      await Promise.all(
        routeIds.map(routeId =>
          assignRouteToTask({
            envId,
            routeId,
            taskId: selectedTaskId,
          }).unwrap(),
        ),
      );

      // Assign implement
      const routesToAssignBeacon = routesData.reduce(
        (accumulator: { routeId: number; beaconId: string }[], routeData) => {
          const originalRoute = routes.find(
            route => route.id === routeData.routeId,
          );
          if (
            originalRoute &&
            routeData.beaconId &&
            originalRoute.beaconId !== routeData.beaconId
          ) {
            accumulator.push({
              routeId: routeData.routeId,
              beaconId: routeData.beaconId,
            });
          }
          return accumulator;
        },
        [],
      );

      await Promise.all(
        routesToAssignBeacon.map(({ routeId, beaconId }) =>
          assignBeaconToRoute({
            envId,
            routeId,
            beaconId,
          }).unwrap(),
        ),
      );

      // Assign employee
      const routesToAssignSlim = routesData.reduce(
        (accumulator: { routeId: number; slimId: string }[], routeData) => {
          const originalRoute = routes.find(
            route => route.id === routeData.routeId,
          );
          if (
            originalRoute &&
            routeData.slimId &&
            originalRoute.slimId !== routeData.slimId
          ) {
            accumulator.push({
              routeId: routeData.routeId,
              slimId: routeData.slimId,
            });
          }
          return accumulator;
        },
        [],
      );

      await Promise.all(
        routesToAssignSlim.map(({ routeId, slimId }) =>
          assignSlimToRoute({
            envId,
            routeId,
            slimId,
          }).unwrap(),
        ),
      );

      history.push(
        generatePath(RouteConstants.task.details, {
          envId,
          taskId: selectedTaskId,
        }),
      );

      showAddRouteToTaskSuccessNotification();
    } catch (error) {
      handleError(error);
    }
  };

  const onCreateNewTask = () => {
    const routesData = mapSelectedImplementsEmployeeAndRoouteToPayload({
      groupedRoutes,
      rowsWithSelectedImplement,
      employeeArray,
      implementArray,
      routeTableItems,
    });

    const routesToAdd = routes.filter(route =>
      routesData.find(routeDataItem => routeDataItem.routeId === route.id),
    );

    history.push(
      generatePath(RouteConstants.task.createTask, {
        envId,
      }),
      { routesData, routes: routesToAdd },
    );
  };

  return { onMoveRoutesToTask, onCreateNewTask };
};
